import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  getClientApp,
  isAppAvailable,
  readClientAppOverrides,
  readInstalledApps,
  writeInstalledApps,
  type ClientAppOverrides,
} from '@/lib/clientApps';

export interface SyncStatus {
  offline: boolean;
  syncing: boolean;
  pending: number;
  syncError: string | null;
  onSync: () => void;
}

interface AppShellProps {
  slug: string;
  children: ReactNode;
  sync?: SyncStatus;
  headerExtra?: ReactNode;
}

/**
 * Coque commune aux mini-applications du compte utilisateur :
 * vérifie l'installation, affiche l'état de synchronisation et impose
 * un cadre cohérent (retour App Store, titre, actions).
 */
export default function AppShell({ slug, children, sync, headerExtra }: AppShellProps) {
  const { user } = useAuth();
  const app = getClientApp(slug);
  const [installed, setInstalled] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<ClientAppOverrides>({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [slugs, catalog] = await Promise.all([readInstalledApps(user.id), readClientAppOverrides()]);
      setInstalled(slugs);
      setOverrides(catalog);
    } catch {
      setError('Impossible de vérifier votre installation.');
    } finally {
      setReady(true);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const install = async () => {
    if (!user || !app) return;
    try {
      const next = Array.from(new Set([...installed, app.slug]));
      await writeInstalledApps(user.id, next);
      setInstalled(next);
    } catch {
      setError("L'installation a échoué.");
    }
  };

  if (!app) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-error-warning-line text-3xl text-foreground-300 mb-3"></i>
        <p className="text-sm text-foreground-600">Application introuvable.</p>
        <Link to="/mon-compte/appstore" className="mt-4 text-sm font-medium text-primary-700 no-underline">
          Retour à l'App Store
        </Link>
      </div>
    );
  }

  const available = isAppAvailable(app, overrides);
  const isInstalled = installed.includes(app.slug);

  if (ready && !available) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-6">
        <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-eye-off-line text-xl text-foreground-500"></i>
        </div>
        <h2 className="text-base font-semibold text-foreground-900">Application momentanément indisponible</h2>
        <p className="text-sm text-foreground-500 mt-1 max-w-md">
          Cette application a été masquée par l'administrateur de la plateforme. Vos données locales restent conservées.
        </p>
        <Link to="/mon-compte/appstore" className="mt-4 text-sm font-medium text-primary-700 no-underline">
          Retour à l'App Store
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Barre d'application */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-3 bg-background-50/95 backdrop-blur border-b border-background-200/50 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/mon-compte/appstore"
            className="w-9 h-9 rounded-full border border-background-200/70 flex items-center justify-center text-foreground-500 hover:text-foreground-900 hover:bg-background-100 transition-colors no-underline flex-shrink-0"
            title="Retour à l'App Store"
          >
            <i className="ri-arrow-left-line"></i>
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
                <i className={`${app.icon} text-xs`}></i>
              </span>
              <h1 className="text-base font-bold font-heading text-foreground-950 truncate">{app.name}</h1>
              {app.offline && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-[10px] font-medium text-foreground-500 whitespace-nowrap">
                  <i className="ri-cloud-off-line"></i>
                  Hors ligne
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {headerExtra}
            {sync && (
              <button
                onClick={sync.onSync}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  sync.offline || sync.pending > 0
                    ? 'bg-accent-100 text-accent-900 hover:bg-accent-200'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
                title={sync.syncError || 'État de la synchronisation'}
              >
                {sync.syncing ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : sync.offline ? (
                  <i className="ri-wifi-off-line"></i>
                ) : sync.pending > 0 ? (
                  <i className="ri-time-line"></i>
                ) : (
                  <i className="ri-checkbox-circle-line text-accent-600"></i>
                )}
                {sync.syncing
                  ? 'Synchro…'
                  : sync.offline
                    ? 'Hors ligne'
                    : sync.pending > 0
                      ? `${sync.pending} en attente`
                      : 'Synchronisé'}
              </button>
            )}
          </div>
        </div>
        {sync?.syncError && (
          <p className="mt-2 text-[11px] text-accent-900 bg-accent-100/70 rounded-md px-3 py-1.5">
            <i className="ri-information-line mr-1"></i>
            {sync.syncError}
          </p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-4">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="cursor-pointer" title="Fermer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {ready && app.requiresInstall && !isInstalled ? (
        <div className="flex flex-col items-center justify-center py-16 text-center px-6 border border-dashed border-background-300/70 rounded-xl">
          <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className={`${app.icon} text-2xl text-foreground-500`}></i>
          </div>
          <h2 className="text-base font-semibold text-foreground-900">{app.name} n'est pas encore installée</h2>
          <p className="text-sm text-foreground-500 mt-1.5 max-w-md">{app.description}</p>
          <div className="flex items-center gap-2 mt-5">
            <button
              onClick={install}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-foreground-800 cursor-pointer"
            >
              <i className="ri-download-2-line"></i>
              Installer maintenant
            </button>
            <Link
              to="/mon-compte/appstore"
              className="px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap no-underline hover:bg-background-200/70"
            >
              Parcourir l'App Store
            </Link>
          </div>
        </div>
      ) : (
        children
      )}
    </div>
  );
}